import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { nav, contact } from '../../content/site';
import { ChevronDown, Phone } from './Icons';

import logoOnDark from '../../images/logo-dark.png';
import logoOnLight from '../../images/logo-light.png';

const SOLID_AFTER = 40;

/**
 * Fixed header. Sits transparent over the hero, then turns solid on scroll.
 * `overHero` is false for pages without a dark image behind the header.
 */
const Header = ({ overHero = true }) => {
  const [solid, setSolid] = useState(!overHero);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!overHero) {
      setSolid(true);
      return undefined;
    }
    const onScroll = () => setSolid(window.scrollY > SOLID_AFTER);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [overHero]);

  // Close the drawer on navigation
  useEffect(() => {
    setMenuOpen(false);
    setOpenSubmenu(null);
  }, [pathname]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Escape closes the drawer
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openMenu = useCallback((label) => {
    clearTimeout(closeTimer.current);
    setOpenSubmenu(label);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenSubmenu(null), 140);
  }, []);

  const isSolid = solid || menuOpen;
  const headerClass = [
    'site-header',
    isSolid ? 'site-header--solid' : 'site-header--over',
    menuOpen ? 'site-header--menu-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // On a solid white header the dark-text logo reads; over the hero the
  // white-text version does. The drawer is dark, so flip back.
  const logo = isSolid && !menuOpen ? logoOnLight : logoOnDark;
  const primaryPhone = contact.phones[0];

  return (
    <>
      <header className={headerClass}>
        <div className="site-header__inner">
          <Link to="/" className="site-header__logo" aria-label="LIDA Engineering — home">
            <img src={logo} alt="LIDA Engineering" width="173" height="44" />
          </Link>

          <nav className="nav" aria-label="Main">
            <ul className="nav__list">
              {nav.map((item) => (
                <li
                  key={item.label}
                  className="nav__item"
                  data-open={openSubmenu === item.label ? 'true' : 'false'}
                  onMouseEnter={() => item.children && openMenu(item.label)}
                  onMouseLeave={() => item.children && scheduleClose()}
                >
                  <Link
                    to={item.to}
                    className="nav__link"
                    onFocus={() => item.children && openMenu(item.label)}
                    aria-expanded={item.children ? openSubmenu === item.label : undefined}
                  >
                    {item.label}
                    {item.children && <ChevronDown className="nav__caret" />}
                  </Link>

                  {item.children && (
                    <ul className="nav__submenu">
                      {item.children.map((child) => (
                        <li key={child.to}>
                          <Link
                            to={child.to}
                            className="nav__sublink"
                            onBlur={scheduleClose}
                            onFocus={() => openMenu(item.label)}
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-header__actions">
            <a className="site-header__phone" href={`tel:${primaryPhone.replace(/[^+\d]/g, '')}`}>
              <Phone />
              {primaryPhone}
            </a>
            <Link to="/#contact" className="btn btn--sm">
              Request a Quote
            </Link>
          </div>

          <button
            type="button"
            className="nav-toggle"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="nav-toggle__bars" />
          </button>
        </div>
      </header>

      <div id="mobile-nav" className={`mobile-nav${menuOpen ? ' is-open' : ''}`} hidden={!menuOpen}>
        <nav aria-label="Mobile">
          <ul>
            {nav.map((item) => (
              <li key={item.label}>
                <Link to={item.to} className="mobile-nav__link">
                  {item.label}
                </Link>
                {item.children && (
                  <ul>
                    {item.children.map((child) => (
                      <li key={child.to}>
                        <Link to={child.to} className="mobile-nav__sublink">
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="mobile-nav__footer">
          {contact.phones.map((phone) => (
            <a
              key={phone}
              className="mobile-nav__contact"
              href={`tel:${phone.replace(/[^+\d]/g, '')}`}
            >
              <Phone />
              {phone}
            </a>
          ))}
          <Link to="/#contact" className="btn">
            Request a Quote
          </Link>
        </div>
      </div>
    </>
  );
};

export default Header;
